import { useNavigate, useParams, Link } from "react-router-dom";
import { Check, ArrowRight, ChevronRight } from "lucide-react";
import { Icon } from "@/components/site/Icon";
import { GoldButton, GhostButton } from "@/components/site/ui";
import { useFetch } from "@/lib/useFetch";
import { buyService } from "@/lib/cart";
import { inr, track } from "@/lib/api";
import { useLead } from "@/context/LeadContext";
import { useSeo } from "@/lib/seo";

export default function ServiceDetail() {
    const { slug } = useParams();
    const nav = useNavigate();
    const { openLead } = useLead();
    const { data: s, loading } = useFetch(`/services/${slug}`);
    const { data: faqs } = useFetch("/faqs", []);

    useSeo(s ? {
        title: `${s.name} Services from ${inr(s.starting_price)} | SUDIGI`,
        description: s.description,
        jsonLd: {
            "@context": "https://schema.org", "@type": "Service", name: s.name, description: s.description,
            areaServed: "IN", provider: { "@type": "Organization", name: "SUDIGI" },
            offers: { "@type": "Offer", price: s.starting_price, priceCurrency: "INR" },
        },
    } : { title: "Service | SUDIGI" });

    if (loading) return <div className="min-h-[60vh] grid place-items-center text-cream-soft">Loading…</div>;
    if (!s) return <div className="min-h-[60vh] grid place-items-center text-cream-soft">Service not found.</div>;

    const go = () => { buyService(s.slug); track("cta_click", { cta: "service_detail_buy", slug: s.slug }); nav("/checkout"); };

    return (
        <>
            <section className="bg-gradient-to-b from-teal-950 via-teal-900 to-teal-800 pt-14 pb-16 grain">
                <div className="container-x">
                    <nav className="flex items-center gap-1.5 text-xs text-cream-soft/70 mb-6" aria-label="Breadcrumb">
                        <Link to="/" className="hover:text-gold">Home</Link><ChevronRight className="w-3 h-3" />
                        <Link to="/services" className="hover:text-gold">Services</Link><ChevronRight className="w-3 h-3" />
                        <span className="text-cream">{s.name}</span>
                    </nav>
                    <div className="grid lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <div className="w-14 h-14 rounded-2xl bg-gold/15 border border-gold/30 flex items-center justify-center mb-5"><Icon name={s.icon} className="w-7 h-7 text-gold" /></div>
                            <h1 className="font-serif text-4xl sm:text-5xl leading-tight text-cream">{s.hero_headline || s.name}</h1>
                            <p className="mt-4 text-cream-soft text-lg">{s.tagline}</p>
                            <div className="mt-6 flex items-baseline gap-2">
                                <span className="text-sm text-cream-soft">Starting at</span>
                                <span className="font-mono font-bold text-3xl text-gold">{inr(s.starting_price)}</span>
                            </div>
                            <div className="mt-7 flex flex-wrap gap-3">
                                <GoldButton onClick={go} data-testid="service-detail-buy">{s.cta || "Get Started"} <ArrowRight className="w-4 h-4" /></GoldButton>
                                <GhostButton onClick={() => openLead(s.name)} data-testid="service-detail-talk">Talk to an Expert</GhostButton>
                            </div>
                        </div>
                        <div className="card-teal rounded-3xl p-7">
                            <h3 className="font-serif text-xl text-cream mb-4">What's Included</h3>
                            <ul className="space-y-3">
                                {s.features.map((f) => <li key={f} className="flex items-start gap-2.5 text-sm text-cream-soft"><Check className="w-5 h-5 text-growth shrink-0" /> {f}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-teal-900 py-16">
                <div className="container-x grid md:grid-cols-2 gap-6">
                    <Block title="The Problem" body={s.problem} />
                    <Block title="Our Solution" body={s.solution} accent />
                </div>
            </section>

            {s.who?.length > 0 && (
                <section className="bg-teal-950 py-16 grain">
                    <div className="container-x">
                        <h2 className="font-serif text-3xl text-cream text-center mb-8">Who Is It For?</h2>
                        <div className="flex flex-wrap justify-center gap-3">
                            {s.who.map((w) => <span key={w} className="card-teal rounded-full px-5 py-2.5 text-sm text-cream">{w}</span>)}
                        </div>
                    </div>
                </section>
            )}

            <section className="bg-teal-900 py-16">
                <div className="container-x max-w-3xl">
                    <h2 className="font-serif text-3xl text-cream text-center mb-8">Common Questions</h2>
                    <div className="space-y-3">
                        {(faqs || []).slice(0, 5).map((f, i) => (
                            <div key={i} className="card-teal rounded-xl p-5">
                                <h4 className="font-semibold text-cream text-sm">{f.q}</h4>
                                <p className="text-cream-soft text-sm mt-2">{f.a}</p>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-10">
                        <GoldButton onClick={go} className="!px-8 !py-4">{s.cta || "Get Started"} — {inr(s.starting_price)}</GoldButton>
                    </div>
                </div>
            </section>
        </>
    );
}

function Block({ title, body, accent }) {
    return (
        <div className={`rounded-2xl p-7 ${accent ? "bg-gradient-to-br from-teal-700 to-teal-800 border border-gold/30" : "card-teal"}`}>
            <h3 className="font-serif text-2xl text-cream mb-3">{title}</h3>
            <p className="text-cream-soft">{body}</p>
        </div>
    );
}
